/**
 * DevTools page — Supervisor/Admin-only dev utilities.
 * Provides a one-click HTE data reset across non-production databases only
 * (Edge and the Cloud DB). Production targets (GCP-Prod, Oracle) are intentionally
 * not offered — a dev-tools reset must never be able to touch production data.
 *
 * The current Cloud DB host/name is fetched from /api/env/hosts (admin-only) and shown next to the
 * target selector + in the confirm modal, so the operator can be certain WHICH
 * database they are about to wipe before confirming.
 */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Navigate } from 'react-router-dom';
import { AlertTriangle, Wrench, RotateCcw, CheckCircle, XCircle, Database } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import operatorService from '../../services/operatorService';
import api from '../../services/api';
import DataRepairPanel from './DataRepairPanel';

interface ResetResult {
  status: string;
  target: string;
  edge: Record<string, number>;
  stagingVm: Record<string, number>;
  errors: string[];
}

/** Render a table of deleted row counts for one database */
const DbSection: React.FC<{
  label: string;
  counts: Record<string, number>;
  t: (key: string) => string;
}> = ({ label, counts, t }) => {
  const entries = Object.entries(counts);
  const hasData = entries.some(([, count]) => count > 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Database className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        <h4 className="font-semibold text-gray-800 dark:text-gray-200">{label}</h4>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 pl-6">{t('devTools.noRecords')}</p>
      ) : !hasData ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 pl-6">{t('devTools.noRecords')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-1 pl-6 text-sm">
          {entries.map(([table, count]) => {
            if (table === 'sequences_reset' || table === 'production_sequences_reset') {
              return (
                <div key={table} className="col-span-2 text-green-700 dark:text-green-400 font-medium">
                  {t('devTools.sequencesReset')}: {count}
                </div>
              );
            }
            if (table === 'stations_reset') {
              return (
                <div key={table} className="col-span-2 text-green-700 dark:text-green-400 font-medium">
                  {t('devTools.stationsReset')}: {count}
                </div>
              );
            }
            if (count === 0) return null;
            return (
              <div key={table} className="flex justify-between text-gray-600 dark:text-gray-400">
                <span className="font-mono text-xs">{table}</span>
                <span className="ml-2">{count} {t('devTools.rowsDeleted')}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Available reset targets — production targets are intentionally excluded */
type ResetTarget = 'all' | 'edge' | 'staging-vm';

const DevToolsPanel: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('devTools.title'));

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<ResetTarget>('all');

  // Current Cloud DB host/name (admin-only /api/env/hosts) — shown so the operator
  // can verify EXACTLY which database they are about to wipe before confirming.
  const envQuery = useQuery<{ env: string; cloudEnv?: string; dbName: string }>(
    ['env-info'],
    async () => {
      const { data } = await api.get('/env');
      return data;
    },
    { staleTime: Infinity, refetchOnWindowFocus: false }
  );
  const hostQuery = useQuery<{ dbHost: string; cloudDbHost?: string }>(
    ['env-hosts'],
    async () => {
      const { data } = await api.get('/env/hosts');
      return data;
    },
    { staleTime: Infinity, refetchOnWindowFocus: false }
  );
  const envInfo = envQuery.data;
  const hostInfo = hostQuery.data;
  const needsHostVerification = target !== 'edge';
  const dbTargetVerified = Boolean(envInfo && hostInfo);
  const hostVerificationPending = needsHostVerification
    && (envQuery.isLoading || hostQuery.isLoading);
  const hostVerificationFailed = needsHostVerification
    && (envQuery.isError || hostQuery.isError || (!hostVerificationPending && !dbTargetVerified));
  const resetBlockedForHostVerification = needsHostVerification
    && (hostVerificationPending || hostVerificationFailed);
  // Prefer cloudDbHost (the real cloud host, resolved past any local tunnel) so the
  // operator sees the TRUE reset target; fall back to dbHost for older gateways.
  const dbTarget = envInfo && hostInfo
    ? `${envInfo.dbName}@${hostInfo.cloudDbHost || hostInfo.dbHost}`
    : null;
  // cloudEnv is the reliable env classification of the real host (dev/staging/prod);
  // fall back to the fuzzy env only if an older gateway doesn't return it.
  const cloudEnvLabel = (envInfo?.cloudEnv || envInfo?.env || '').toUpperCase();

  // Friendly label for a reset target value (never surface the raw 'staging-vm' key).
  const targetLabelFor = (v: ResetTarget): string =>
    v === 'all' ? t('devTools.targetAll')
    : v === 'edge' ? t('devTools.targetEdge')
    : t('devTools.targetStagingVm');

  const handleReset = async () => {
    if (confirmText !== 'RESET') return;
    if (resetBlockedForHostVerification) return;

    setIsResetting(true);
    setError(null);
    setResult(null);

    try {
      const data = await operatorService.resetHteData(target);
      setResult(data);
      setShowConfirm(false);
      setConfirmText('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || t('devTools.resetError'));
    } finally {
      setIsResetting(false);
    }
  };

  const isPartial = result?.status === 'partial';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Wrench className="h-7 w-7 text-gray-500 dark:text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('devTools.title')}</h1>
      </div>

      <DataRepairPanel />

      {/* Danger Zone card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-red-200 dark:border-red-800 shadow-sm">
        <div className="px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-t-lg">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">{t('devTools.dangerZone')}</h2>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{t('devTools.dangerZoneDescription')}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Reset HTE Data */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-gray-100">{t('devTools.resetHteData')}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('devTools.resetDescription')}</p>
              </div>
              <button
                onClick={() => { setShowConfirm(true); setResult(null); setError(null); }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md text-sm whitespace-nowrap transition-colors"
                disabled={isResetting}
              >
                <RotateCcw className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                {t('devTools.resetHteData')}
              </button>
            </div>

            {/* Target selector */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('devTools.targetLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {([
                  { value: 'all', label: t('devTools.targetAll') },
                  { value: 'edge', label: t('devTools.targetEdge') },
                  { value: 'staging-vm', label: t('devTools.targetStagingVm') },
                ] as { value: ResetTarget; label: string }[]).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setTarget(value)}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                      target === value
                        ? 'bg-red-100 dark:bg-red-900/30 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 font-medium'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Show the resolved Cloud DB host so it's unambiguous which DB the
                  'all' / cloud target will hit in this environment. */}
              {dbTarget && target !== 'edge' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {t('devTools.cloudDbTarget')}{' '}
                  <span className="font-mono text-red-600 dark:text-red-400">{dbTarget}</span>
                  {cloudEnvLabel && (
                    <span className="ml-1 text-gray-400 dark:text-gray-500">({cloudEnvLabel})</span>
                  )}
                </p>
              )}
              {hostVerificationPending && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                  {t('devTools.hostVerifyLoading')}
                </p>
              )}
              {hostVerificationFailed && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {t('devTools.hostVerifyError')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-6 w-6 flex-shrink-0" />
              <h3 className="text-lg font-semibold">{t('devTools.resetConfirmTitle')}</h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-300">{t('devTools.resetConfirmWarning')}</p>

            <div className="bg-gray-100 dark:bg-gray-700 rounded-md px-3 py-2 text-sm space-y-1">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">{t('devTools.targetLabel')}</span>{' '}
                <span className="font-mono text-red-600 dark:text-red-400">{targetLabelFor(target)}</span>
              </div>
              {dbTarget && target !== 'edge' && (
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">{t('devTools.cloudDbTarget')}</span>{' '}
                  <span className="font-mono text-red-600 dark:text-red-400">{dbTarget}</span>
                  {cloudEnvLabel && (
                    <span className="ml-1 text-gray-400 dark:text-gray-500">({cloudEnvLabel})</span>
                  )}
                </div>
              )}
              {hostVerificationPending && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t('devTools.hostVerifyLoading')}
                </p>
              )}
              {hostVerificationFailed && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {t('devTools.hostVerifyError')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('devTools.typeToConfirm')}
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="RESET"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => { setShowConfirm(false); setConfirmText(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                disabled={isResetting}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReset}
                disabled={confirmText !== 'RESET' || isResetting || resetBlockedForHostVerification}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResetting ? t('devTools.resetting') : t('devTools.resetButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success / Partial result */}
      {result && (
        <div className={`${isPartial
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
          : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        } border rounded-lg p-6 space-y-5`}>
          <div className={`flex items-center gap-2 ${isPartial
            ? 'text-yellow-700 dark:text-yellow-400'
            : 'text-green-700 dark:text-green-400'
          }`}>
            <CheckCircle className="h-5 w-5" />
            <h3 className="font-semibold">
              {isPartial ? t('devTools.resetPartial') : t('devTools.resetSuccess')}
            </h3>
          </div>

          {/* Errors from individual databases */}
          {result.errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 space-y-1">
              {result.errors.map((err, i) => (
                <p key={i} className="text-sm text-red-600 dark:text-red-400">{err}</p>
              ))}
            </div>
          )}

          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('devTools.resetSummary')}</p>

          {/* Edge Local DB — only show if edge was targeted */}
          {Object.keys(result.edge).length > 0 && (
            <DbSection label={t('devTools.edgeLocal')} counts={result.edge} t={t} />
          )}

          {/* Staging VM — only show if staging-vm was targeted */}
          {Object.keys(result.stagingVm).length > 0 && (
            <DbSection label={t('devTools.stagingVm')} counts={result.stagingVm} t={t} />
          )}
        </div>
      )}

      {/* Error (total failure) */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-red-700 dark:text-red-400">{t('devTools.resetError')}</h3>
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * WCO-4142: Dev Tools only exists where operator-service runs with DEV_TOOLS_ENABLED=true (dev /
 * staging). Everywhere else — including production — the page sends the user home. The API
 * independently 404s every dev-tools route, so hiding the page is UX; the server is the control.
 * Renders nothing while the check is in flight so the danger zone never flashes; a failed check is
 * treated as "off".
 */
const DevTools: React.FC = () => {
  const { data, isLoading } = useQuery(['dev-tools-status'], operatorService.getDevToolsStatus, {
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  if (isLoading) return null;
  if (!data?.enabled) return <Navigate to="/" replace />;
  return <DevToolsPanel />;
};

export default DevTools;
